import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { ProbeRegionStatus, ProbeTargetStatus, REGION_LABELS } from '../../hooks/useProbeMetrics';

// ── Types ─────────────────────────────────────────────────────────────────────

interface MonitorRegionCardsProps {
  targets: ProbeTargetStatus[];
  loading?: boolean;
  provisionedRegions: string[];
  configuredTargets: string[];
}

interface HistoryPoint {
  durationMs:  number | null;
  success:     boolean;
  statusCode:  number | null;
  errorType:   string | null;
  checkedAt:   string;
}

// ── Absolute thresholds ───────────────────────────────────────────────────────
// Bar color & height use an absolute time scale shared across all regions.
// 0–10 s → green | 10–30 s → yellow | 30 s+ / error → red

const SCALE_MAX_MS    = 10_000;   // bar height maxes out at 10 s
const THRESHOLD_SLOW  = 10_000;   // ≥ 10 s → slow/yellow
const THRESHOLD_TIMEOUT = 30_000; // ≥ 30 s → timeout/red

type StatusLevel = 'success' | 'slow' | 'failure';

/** HTTP status code is ground truth; success boolean can lag in Prometheus. */
function effectiveSuccess(success: boolean, statusCode: number | null): boolean {
  if (statusCode != null && statusCode >= 400) return false;
  if (statusCode != null && statusCode >= 200) return true;
  return success;
}

function absoluteBarColor(ms: number | null, success: boolean, statusCode: number | null = null): string {
  if (ms == null) return '#ef4444';
  if (!effectiveSuccess(success, statusCode)) return '#ef4444';
  if (ms >= THRESHOLD_TIMEOUT) return '#ef4444';        // 30 s+ → red
  if (ms >= THRESHOLD_SLOW)    return '#f59e0b';        // 10–30 s → yellow
  return '#10b981';                                     // 0–10 s → green
}

function absoluteBarHeight(ms: number | null, maxH: number): number {
  if (ms == null) return maxH;                          // error → full bar (red)
  return Math.max(3, Math.min(maxH, Math.round((ms / SCALE_MAX_MS) * maxH)));
}

function pointStatusLevel(p: Pick<HistoryPoint, 'success' | 'durationMs' | 'statusCode'>): StatusLevel {
  if (p.statusCode != null && p.statusCode >= 400) return 'failure';
  if (!effectiveSuccess(p.success, p.statusCode)) return 'failure';
  if (p.durationMs != null && p.durationMs >= THRESHOLD_SLOW) return 'slow';
  return 'success';
}

function formatStatusLabel(p: Pick<HistoryPoint, 'success' | 'statusCode' | 'durationMs' | 'errorType'>): string {
  if (p.statusCode != null) return `HTTP ${p.statusCode}`;
  if (p.errorType === 'timeout')            return 'Timeout';
  if (p.errorType === 'dns')                return 'DNS Error';
  if (p.errorType === 'connection_refused') return 'Conn. Refused';
  if (p.errorType === 'connection_reset')   return 'Conn. Reset';
  if (p.errorType === 'host_unreachable')   return 'Unreachable';
  if (p.errorType === 'tls_error')          return 'TLS Error';
  if (p.errorType === 'connection')         return 'TCP Error';
  if (p.errorType === 'http_error')         return 'HTTP Error';
  if (!p.success) {
    if (p.durationMs == null)   return 'No Data';
    if (p.durationMs >= 30_000) return 'Timeout';
    if (p.durationMs < 200)     return 'Conn. Refused';
    return 'TCP Error';
  }
  return '—';
}

/** Short tooltip explanation shown on hover for each error label. */
const ERROR_TOOLTIP: Record<string, string> = {
  'Timeout':       'Server did not respond within the time limit',
  'DNS Error':     'Domain name could not be resolved',
  'Conn. Refused': 'Server rejected the connection — port closed or firewall',
  'Conn. Reset':   'Server dropped the connection unexpectedly',
  'Unreachable':   'No route to the target host or network',
  'TLS Error':     'SSL/TLS handshake failed',
  'TCP Error':     'Low-level network error',
  'HTTP Error':    'Server returned an unexpected error status code',
  'No Data':       'No probe data received yet',
};

const STATUS_CFG: Record<StatusLevel, { label: string; color: string; bgHeader: string; bgCard: string; Icon: React.ElementType }> = {
  success: { label: 'Available', color: '#10b981', bgHeader: 'rgba(16,185,129,0.12)', bgCard: 'rgba(16,185,129,0.03)', Icon: CheckCircleIcon },
  slow:    { label: 'Slow',      color: '#f59e0b', bgHeader: 'rgba(245,158,11,0.12)',  bgCard: 'rgba(245,158,11,0.03)',  Icon: WarningIcon  },
  failure: { label: 'Down',      color: '#ef4444', bgHeader: 'rgba(239,68,68,0.12)',   bgCard: 'rgba(239,68,68,0.03)',   Icon: ErrorIcon    },
};

const LOADING_COLOR = '#4b5563';
const LOADING_BG_H  = 'rgba(75,85,99,0.10)';

// ── Sparkline ─────────────────────────────────────────────────────────────────
// Layout: [fluid-width bars ←→ big number pinned RIGHT]

const BAR_COUNT = 14;
const CHART_H   = 42;
// SVG viewBox width — rendered at 100% of the flex area so bars stretch to
// reach the number.  Bars use percentage-based x positions.
const VB_W = 280;
const VB_BAR_GAP = 3;
const VB_BAR_W   = (VB_W - (BAR_COUNT - 1) * VB_BAR_GAP) / BAR_COUNT;

interface HoveredBar { point: HistoryPoint; mouseX: number; mouseY: number; }

const formatDuration = (ms: number | null): { value: string; unit: string } => {
  if (ms == null) return { value: '—', unit: '' };
  if (ms >= 1000)  return { value: (ms / 1000).toFixed(1), unit: 's' };
  return { value: ms.toString(), unit: 'ms' };
};

const MiniSparkline: React.FC<{ history: HistoryPoint[] }> = ({ history }) => {
  const [hovered, setHovered] = useState<HoveredBar | null>(null);

  const latest   = history[history.length - 1] ?? null;
  const latLevel = latest ? pointStatusLevel(latest) : 'failure';
  const latColor = latest ? absoluteBarColor(latest.durationMs, latest.success, latest.statusCode) : '#6b7280';
  const latDur   = formatDuration(latest?.durationMs ?? null);
  const latLabel = latest ? formatStatusLabel(latest) : '—';

  const padded: (HistoryPoint | null)[] = [
    ...Array(Math.max(0, BAR_COUNT - history.length)).fill(null),
    ...history.slice(-BAR_COUNT),
  ];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      {/* Fluid bar chart — grows to fill space left of the number */}
      <Box sx={{ flex: 1, minWidth: 0, mr: 0.75 }}>
        <svg
          width="100%"
          viewBox={`0 0 ${VB_W} ${CHART_H}`}
          preserveAspectRatio="none"
          style={{ display: 'block' }}
        >
          {padded.map((point, i) => {
            const x      = i * (VB_BAR_W + VB_BAR_GAP);
            const isLast = i === padded.length - 1;
            if (!point) {
              return <rect key={i} x={x} y={0} width={VB_BAR_W} height={CHART_H} fill="rgba(255,255,255,0.04)" rx={1} />;
            }
            const c = absoluteBarColor(point.durationMs, point.success, point.statusCode);
            const h = absoluteBarHeight(point.durationMs, CHART_H);
            const opacity = isLast ? 1 : 0.22 + (i / (BAR_COUNT - 1)) * 0.65;
            return (
              <rect
                key={i}
                x={x} y={CHART_H - h} width={VB_BAR_W} height={h}
                fill={c} rx={1} opacity={opacity}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => setHovered({ point, mouseX: e.clientX, mouseY: e.clientY })}
                onMouseMove={(e)  => setHovered((p) => p ? { ...p, mouseX: e.clientX, mouseY: e.clientY } : null)}
                onMouseLeave={()  => setHovered(null)}
              />
            );
          })}
        </svg>
      </Box>

      {/* Big value — pinned right, fixed width */}
      <Box sx={{ flexShrink: 0, textAlign: 'right', mb: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 0.2 }}>
          <Typography sx={{
            fontSize: '1.85rem', fontWeight: 800,
            fontFamily: '"Space Mono", monospace',
            color: latColor, lineHeight: 1,
          }}>
            {latDur.value}
          </Typography>
          {latDur.unit && (
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 700,
              fontFamily: '"Space Mono", monospace',
              color: latColor, opacity: 0.8, lineHeight: 1,
            }}>
              {latDur.unit}
            </Typography>
          )}
        </Box>
        <Tooltip
          title={ERROR_TOOLTIP[latLabel] ?? ''}
          placement="left"
          disableHoverListener={!ERROR_TOOLTIP[latLabel]}
          arrow
        >
          <Typography sx={{
            fontSize: '0.75rem', fontWeight: 700,
            fontFamily: '"Space Mono", monospace',
            textAlign: 'right',
            color: latLevel === 'failure' ? STATUS_CFG.failure.color
                 : latLevel === 'slow'    ? STATUS_CFG.slow.color
                 : 'text.secondary',
            mt: 0.3,
            cursor: ERROR_TOOLTIP[latLabel] ? 'help' : 'default',
            textDecoration: ERROR_TOOLTIP[latLabel] ? 'underline dotted' : 'none',
            textUnderlineOffset: '2px',
          }}>
            {latLabel}
          </Typography>
        </Tooltip>
      </Box>

      {/* Hover tooltip */}
      {hovered && createPortal((() => {
        const c    = absoluteBarColor(hovered.point.durationMs, hovered.point.success, hovered.point.statusCode);
        const time = new Date(hovered.point.checkedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        const dur  = formatDuration(hovered.point.durationMs);
        const sLbl = formatStatusLabel(hovered.point);
        return (
          <div style={{
            position: 'fixed', left: hovered.mouseX, top: hovered.mouseY - 12,
            transform: 'translate(-50%,-100%)', zIndex: 9999, pointerEvents: 'none',
            background: 'rgba(15,23,42,0.97)', border: `1px solid ${c}66`,
            borderRadius: 6, padding: '6px 10px', minWidth: 110,
            fontFamily: '"Space Mono", monospace',
          }}>
            <div style={{ fontSize: '0.76rem', fontWeight: 700, color: c, lineHeight: 1.2 }}>
              {dur.value}{dur.unit ? ` ${dur.unit}` : ''}
            </div>
            <div style={{ fontSize: '0.62rem', color: '#9ca3af', marginTop: 3 }}>{sLbl}</div>
            {ERROR_TOOLTIP[sLbl] && (
              <div style={{ fontSize: '0.58rem', color: '#6b7280', marginTop: 2, fontStyle: 'italic' }}>{ERROR_TOOLTIP[sLbl]}</div>
            )}
            <div style={{ fontSize: '0.58rem', color: '#6b7280', marginTop: 2 }}>{time}</div>
          </div>
        );
      })(), document.body)}
    </Box>
  );
};

// ── Skeleton ──────────────────────────────────────────────────────────────────

const SparklineSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
    <Skeleton variant="rounded" sx={{ flex: 1, mr: 0.75, height: CHART_H }} />
    <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
      <Skeleton width={52} height={28} />
      <Skeleton width={42} height={12} sx={{ mt: 0.25, ml: 'auto' }} />
    </Box>
  </Box>
);

// ── localStorage ──────────────────────────────────────────────────────────────

const HIDDEN_KEY = 'monitor_hidden_regions';

function loadHiddenRegions(): Set<string> {
  try {
    const raw = localStorage.getItem(HIDDEN_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}

function saveHiddenRegions(set: Set<string>): void {
  try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(Array.from(set))); } catch { /* ignore */ }
}

// ── Main component ────────────────────────────────────────────────────────────

const MonitorRegionCards: React.FC<MonitorRegionCardsProps> = ({
  targets,
  loading = false,
  provisionedRegions,
  configuredTargets,
}) => {
  const historyRef = useRef<Map<string, HistoryPoint[]>>(new Map());
  const [, setTick] = useState(0);
  const [hiddenRegions, setHiddenRegions] = useState<Set<string>>(loadHiddenRegions);

  const toggleHidden = (code: string) => {
    setHiddenRegions((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      saveHiddenRegions(next);
      return next;
    });
  };

  useEffect(() => {
    let changed = false;
    for (const t of targets) {
      for (const r of t.regions) {
        const k        = `${r.target}||${r.region}`;
        const existing = historyRef.current.get(k) ?? [];
        const lastTs   = existing[existing.length - 1]?.checkedAt;
        if (r.checkedAt && r.checkedAt !== lastTs) {
          const next: HistoryPoint[] = [
            ...existing,
            { durationMs: r.durationMs, success: r.success, statusCode: r.statusCode, errorType: r.errorType, checkedAt: r.checkedAt },
          ];
          if (next.length > BAR_COUNT) next.shift();
          historyRef.current.set(k, next);
          changed = true;
        }
      }
    }
    if (changed) setTick((n) => n + 1);
  }, [targets]);

  const dataMap = new Map<string, ProbeRegionStatus>();
  for (const t of targets) {
    for (const r of t.regions) dataMap.set(`${r.target}||${r.region}`, r);
  }

  if (provisionedRegions.length === 0) {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 2 }}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">Loading monitoring data…</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="text.secondary">No monitor unit active</Typography>
      </Box>
    );
  }

  type TargetRow = { url: string; data: ProbeRegionStatus | null; history: HistoryPoint[]; hasData: boolean; };
  type RegionEntry = {
    code: string; label: string; subLabel: string;
    worstLevel: StatusLevel; upCount: number;
    allLoading: boolean; rows: TargetRow[];
  };

  const regionEntries: RegionEntry[] = provisionedRegions.map((code) => {
    const rows: TargetRow[] = configuredTargets.map((url) => {
      const k       = `${url}||${code}`;
      const data    = dataMap.get(k) ?? null;
      const history = historyRef.current.get(k) ?? [];
      let displayHistory = history;
      if (data && (history.length === 0 || history[history.length - 1].checkedAt !== data.checkedAt)) {
        displayHistory = [...history, {
          durationMs: data.durationMs, success: data.success,
          statusCode: data.statusCode, errorType: data.errorType, checkedAt: data.checkedAt,
        }].slice(-BAR_COUNT);
      }
      return { url, data, history: displayHistory, hasData: !!data || history.length > 0 };
    });

    const upCount    = rows.filter((r) => r.data && r.data.success && (r.data.statusCode == null || r.data.statusCode < 400)).length;
    const allLoading = configuredTargets.length > 0 && rows.every((r) => !r.hasData);
    let worstLevel: StatusLevel = 'success';
    for (const row of rows) {
      if (!row.data) continue;
      const lvl = pointStatusLevel(row.data);
      if (lvl === 'failure') { worstLevel = 'failure'; break; }
      if (lvl === 'slow') worstLevel = 'slow';
    }

    return {
      code,
      label:    REGION_LABELS[code] ?? code,
      subLabel: code,
      worstLevel, upCount, allLoading, rows,
    };
  });

  const statusOrder: Record<StatusLevel, number> = { failure: 0, slow: 1, success: 2 };
  regionEntries.sort((a, b) => {
    const aH = hiddenRegions.has(a.code), bH = hiddenRegions.has(b.code);
    if (aH !== bH) return aH ? 1 : -1;
    if (a.allLoading !== b.allLoading) return a.allLoading ? 1 : -1;
    if (statusOrder[a.worstLevel] !== statusOrder[b.worstLevel]) return statusOrder[a.worstLevel] - statusOrder[b.worstLevel];
    return a.label.localeCompare(b.label);
  });

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',
        sm: 'repeat(3, 1fr)',
        md: 'repeat(4, 1fr)',
        lg: 'repeat(5, 1fr)',
      },
      gap: 1,
      p: 1.5,
    }}>
      {regionEntries.map((region) => {
        const isLoading = region.allLoading;
        const isHidden  = hiddenRegions.has(region.code);
        const cfg   = isLoading
          ? { label: 'Loading…', color: LOADING_COLOR, bgHeader: LOADING_BG_H, bgCard: 'transparent', Icon: CircularProgress }
          : STATUS_CFG[region.worstLevel];
        const color = isHidden ? '#374151' : cfg.color;

        return (
          <Card
            key={region.code}
            variant="outlined"
            sx={{
              borderColor: `${color}50`,
              background:  isHidden ? 'rgba(31,41,55,0.4)' : cfg.bgCard,
              overflow:    'hidden',
              transition:  'border-color 0.2s, opacity 0.2s',
              opacity:     isHidden ? 0.4 : 1,
              '&:hover':   { borderColor: color, opacity: 1 },
            }}
          >
            {/* ── Header ── */}
            <Box sx={{
              px: 1.25, py: 0.55,
              background:   isHidden ? 'rgba(55,65,81,0.3)' : cfg.bgHeader,
              borderBottom: `1px solid ${color}25`,
              display: 'flex', alignItems: 'center', gap: 0.5,
            }}>
              {isLoading && !isHidden
                ? <CircularProgress size={10} sx={{ color: LOADING_COLOR, flexShrink: 0 }} />
                : <cfg.Icon sx={{ fontSize: '0.85rem', color, flexShrink: 0 }} />}

              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <Typography sx={{
                  fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.2,
                  color: isHidden ? '#6b7280' : isLoading ? '#9ca3af' : 'text.primary',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {region.label}
                </Typography>
                <Typography sx={{ fontSize: '0.58rem', color: '#4b5563', fontFamily: '"Space Mono", monospace', lineHeight: 1 }}>
                  {region.subLabel}
                </Typography>
              </Box>

              {!isLoading && !isHidden && (
                <Box sx={{
                  px: 0.6, py: 0.15, mr: 0.25,
                  borderRadius: 0.75,
                  bgcolor: `${color}18`,
                  border: `1px solid ${color}40`,
                  fontSize: '0.58rem',
                  fontFamily: '"Space Mono", monospace',
                  color, flexShrink: 0, whiteSpace: 'nowrap',
                }}>
                  {cfg.label}&nbsp;{region.upCount}/{region.rows.length}
                </Box>
              )}

              <Tooltip title={isHidden ? 'Show region' : 'Hide region'} placement="top">
                <IconButton
                  size="small"
                  onClick={() => toggleHidden(region.code)}
                  sx={{
                    p: 0.25, flexShrink: 0,
                    color: isHidden ? '#4b5563' : '#374151',
                    '&:hover': { color: isHidden ? color : '#9ca3af' },
                  }}
                >
                  {isHidden
                    ? <ShowIcon sx={{ fontSize: '0.8rem' }} />
                    : <HideIcon sx={{ fontSize: '0.8rem' }} />}
                </IconButton>
              </Tooltip>
            </Box>

            {/* ── Body ── */}
            {!isHidden && (
              <Box sx={{ px: 1.25, pt: 0.75, pb: 1 }}>
                {region.rows.length === 0 && (
                  <Typography sx={{ fontSize: '0.62rem', color: '#4b5563', fontFamily: '"Space Mono", monospace', py: 0.5 }}>
                    No targets configured
                  </Typography>
                )}
                {region.rows.map((row, idx) => (
                  <Box
                    key={row.url}
                    sx={{
                      pt: idx > 0 ? 1 : 0,
                      mt: idx > 0 ? 1 : 0,
                      borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    }}
                  >
                    <Typography sx={{
                      fontSize: '0.6rem', fontFamily: '"Space Mono", monospace',
                      color: 'text.secondary',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      mb: 0.5,
                    }} title={row.url}>
                      {row.url}
                    </Typography>
                    {row.hasData
                      ? <MiniSparkline history={row.history} />
                      : <SparklineSkeleton />}
                  </Box>
                ))}
              </Box>
            )}
          </Card>
        );
      })}
    </Box>
  );
};

export default MonitorRegionCards;
